import type { ApiResponse, ListContactAdsResponse } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';

/** Shared key so any future ad mutation can target the same cache entry. */
export const contactAdsQueryKey = (creatorIdentityId: string | null) =>
  ['contact-ads', creatorIdentityId] as const;

const EMPTY_RESPONSE: ListContactAdsResponse = {
  data: [],
  emailChannel: { available: false, blocker: null },
};

const fetchContactAds = async (creatorIdentityId: string): Promise<ListContactAdsResponse> => {
  const { data } = await api.get<ApiResponse<ListContactAdsResponse>>(
    `/dashboard/contacts/${creatorIdentityId}/ads`
  );

  return data.data ?? EMPTY_RESPONSE;
};

/**
 * Lazy partnership-ads slice for the unified contact card, keyed by
 * `creatorIdentityId`. Only fires when the PUBLICITÉ tab mounts. The list is a
 * small, brand-scoped set of authorizations (unpaginated), so this is a plain
 * `useQuery`. Returns the whole response: the mailbox verdict travels with it,
 * once for the contact, and the composer's channel gating reads it instead of
 * mapping error codes back into a reason after a failed click.
 */
export const useContactAdsQuery = (creatorIdentityId: string | null, enabled = true) => {
  return useQuery({
    queryKey: contactAdsQueryKey(creatorIdentityId),
    queryFn: () => fetchContactAds(creatorIdentityId!),
    enabled: !!creatorIdentityId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
